import { decamelizeKeys } from 'humps';

import type { CustomerProfile } from '@/types/customerProfile';
import type { User } from '@/types/user';

import { apiClient, setAuthTokens } from '../apiClient';
import { authClient } from '../authClient';

const PATHS = {
  /**
   * Tastypie-style paginated list, filtered server-side to the bearer's
   * own user — `objects` always contains exactly one entry. `fetchUser`
   * unwraps to a single `User` so callers don't see the pagination shell.
   */
  CURRENT_USER: '/api/v3/user/',
  AUTH_SIGNUP: '/v1/auth/signup',
  SSO_COMPLETE: '/api/v1/onboarding/sso/complete/',
} as const;

// /api/v3/user/ returns a Tastypie-style paginated list filtered server-side
// to the bearer's own user — `objects` always has exactly one entry. The
// type lives here (not exported) so the public surface is just `User`.
type CurrentUserListResponse = {
  meta: {
    totalCount: number;
    limit: number;
    offset: number;
    next: string | null;
    previous: string | null;
  };
  objects: User[];
};

export async function fetchUser(signal?: AbortSignal): Promise<User> {
  const res = await apiClient.get<CurrentUserListResponse>(PATHS.CURRENT_USER, { signal });
  const user = res.data.objects[0];
  if (!user) {
    throw new Error('user endpoint (v3) returned no objects.');
  }
  return user;
}

export type CreateUserPayload = {
  firstName?: string;
  lastName?: string;
  customerProfile: {
    birthday: string;
    smsAllowed: boolean;
    termsAndConditionsAccepted: boolean;
    informedConsentAccepted: boolean;
    preferredLanguage: string;
  };
  email?: string;
  password: string;
  phone: string;
  timezone: string;
  // UUIDs from GET /api/v1/legal-documents/. The server records signature
  // events per document; the customerProfile booleans above are legacy
  // aggregates and not enough on their own.
  signedDocuments?: { uuid: string }[];
};

// The signup endpoint returns the freshly created user with the same shape
// `/api/v3/user/` does, plus three top-level token fields needed to switch
// the apiClient into authenticated mode before the next request.
type SignupResponse = User & {
  vidaJwtToken: string;
  vidaRefreshToken: string;
  vidaTokenExpires: string;
};

export async function createUser(payload: CreateUserPayload): Promise<User> {
  const res = await authClient.post<SignupResponse>(PATHS.AUTH_SIGNUP, decamelizeKeys(payload));
  const { vidaJwtToken, vidaRefreshToken, vidaTokenExpires, ...user } = res.data;
  await setAuthTokens({
    token: vidaJwtToken,
    refresh: vidaRefreshToken,
    expires: vidaTokenExpires,
  });
  return user;
}

// PATCH a subset of the customer profile by resource_uri. Two callers, two
// field sets — so the fields are a Partial:
//   - Account creation (SSO path): DOB, consents, language.
//   - Create profile: height (total inches), weight (lbs), gender, ethnicity.
// Each caller assembles its own subset via a build* helper; the network layer
// just decamelizes and PATCHes whatever it's given.
export type UpdateCustomerProfilePayload = {
  customerProfileUri: string;
  customerProfile: Partial<{
    birthday: string;
    smsAllowed: boolean;
    termsAndConditionsAccepted: boolean;
    informedConsentAccepted: boolean;
    preferredLanguage: string;
    height: number;
    weight: number;
    gender: string;
    ethnicity: string;
  }>;
};

export async function updateCustomerProfile(
  payload: UpdateCustomerProfilePayload
): Promise<CustomerProfile> {
  const { customerProfileUri, customerProfile } = payload;
  const res = await apiClient.patch<CustomerProfile>(
    customerProfileUri,
    decamelizeKeys(customerProfile)
  );
  return res.data;
}

export type SsoCompletePayload = {
  dob: string;
  password: string;
  smsAllowed: boolean;
  preferredLanguage: string;
  signedDocuments?: { uuid: string }[];
};

export type SsoCompleteStatus =
  | 'complete'
  | 'dob_mismatch'
  | 'invalid_password'
  | 'invalid'
  | 'forbidden';

export type SsoCompleteResponse = {
  status: SsoCompleteStatus;
};

export async function ssoComplete(payload: SsoCompletePayload): Promise<SsoCompleteResponse> {
  try {
    const res = await apiClient.post<SsoCompleteResponse>(
      PATHS.SSO_COMPLETE,
      decamelizeKeys(payload)
    );
    return res.data;
  } catch (err: unknown) {
    // The endpoint returns 400/403 with a { status: "..." } body for
    // dob_mismatch / invalid_password / invalid / forbidden — axios throws
    // on non-2xx, so we must read the status from the error response here
    // rather than from res.data. Genuine network/5xx errors (no response or
    // no status field) are re-thrown so callers still show the generic banner.
    const response = (err as { response?: { data?: { status?: SsoCompleteStatus } } }).response;
    const status = response?.data?.status;
    if (status) {
      return { status };
    }
    throw err;
  }
}
